/*
  Manmukh Ayurveda Enterprise Framework
  File: assets/ma-3d.js
  Purpose: 3D Design System merge — toggle helpers (Phase 1) + the
  Doctor/Treatment card tilt engine (Phase 3).

  TOGGLE HELPERS (Phase 1)
    ManmukhAyurveda.is3DEnabled()
      -> true only when the merchant's Theme Editor toggle (Theme
         Settings -> Manmukh Ayurveda Brand -> "Enable 3D Material &
         Motion Effects", settings.ma_enable_3d_effects) is on. Read
         from <html data-ma-3d="on|off">, set in layout/theme.liquid.
         Other heavy engines (Rotating Card Ring v2 in Phase 2, Medical
         Hub 3D objects in Phase 5) call this before constructing
         themselves at all.

    ManmukhAyurveda.shouldAutoAnimate3D()
      -> true only when is3DEnabled() AND prefers-reduced-motion is NOT
         set. Governs continuous/idle loops specifically (ring
         auto-rotate, Medical Hub 3D object spin/sway/bob). Discrete,
         user-initiated interaction (hover/drag tilt) is allowed to keep
         working even when this returns false, matching the Design
         System reference's own accessibility rule.

  TILT ENGINE (Phase 3)
    Binds mousemove/mouseleave to every [data-ma-3d-tilt] element and
    writes --ma-3d-tilt-x/--ma-3d-tilt-y + sheen position. Needs no
    on/off branching of its own: ma-card.liquid only ever renders the
    data-ma-3d-tilt attribute on Doctor/Treatment cards when the master
    toggle is already on, so an empty scanTilt() result on toggle-off
    pages IS the fallback. prefers-reduced-motion is honored by reading
    the *computed* --ma-3d-tilt-max token (ma-3d.css collapses it to
    0deg under that media query) rather than a second JS-side check.

  This file extends the existing window.ManmukhAyurveda namespace using
  the same Object.assign merge pattern ma-core.js itself uses to export
  its own public API. It never edits ma-core.js — every existing
  ManmukhAyurveda.* helper (accordions, tabs, modals, sliders,
  prefersReducedMotion, etc.) continues to work exactly as before.
  Loaded after ma-core.js in layout/theme.liquid so
  ManmukhAyurveda.prefersReducedMotion is already available to reuse; a
  safe inline fallback covers the case where a section somehow loads
  this file first.
*/

(function () {
  "use strict";

  var namespace = "ManmukhAyurveda";
  var root = document.documentElement;

  function is3DEnabled() {
    return root.getAttribute("data-ma-3d") === "on";
  }

  function prefersReducedMotion() {
    var ns = window[namespace];
    if (ns && typeof ns.prefersReducedMotion === "function") {
      return ns.prefersReducedMotion();
    }
    return !!(
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }

  function shouldAutoAnimate3D() {
    return is3DEnabled() && !prefersReducedMotion();
  }

  /* =======================================================================
     PHASE 3 — Doctor / Treatment card tilt engine.

     No selector-based "is this a doctor or treatment card?" logic lives
     here — ma-card.liquid already decided that server-side and only
     emits [data-ma-3d-tilt] on the two approved families, and only when
     settings.ma_enable_3d_effects is on. That means:
       - scanTilt() finding zero elements IS the toggle-off fallback.
         There is no separate "if 3D disabled, skip" branch to remember
         here — the markup simply doesn't exist in that case.
       - prefers-reduced-motion is handled by reading the *computed*
         --ma-3d-tilt-max token at move-time (ma-3d.css collapses it to
         0deg under that media query and under [data-ma-3d="off"]); if
         it's 0 the rotation is skipped, matching the Design System's
         "interaction still works via discrete steps" rule (the
         box-shadow hover feedback is pure CSS and keeps working
         regardless).
     ======================================================================= */
  var TILT_SELECTOR = "[data-ma-3d-tilt]";
  var tiltBound = typeof WeakSet !== "undefined" ? new WeakSet() : null;

  function tiltMax(el) {
    var raw = getComputedStyle(el).getPropertyValue("--ma-3d-tilt-max");
    return parseFloat(raw) || 0;
  }

  function onTiltMove(el, e) {
    var max = tiltMax(el);
    if (!max) return; // toggle off / reduced-motion already collapsed this to 0deg
    var rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    var px = (e.clientX - rect.left) / rect.width;
    var py = (e.clientY - rect.top) / rect.height;
    var rotY = (px - 0.5) * 2 * max;
    var rotX = (0.5 - py) * 2 * max * 0.8;
    el.style.setProperty("--ma-3d-tilt-x", rotX.toFixed(2) + "deg");
    el.style.setProperty("--ma-3d-tilt-y", rotY.toFixed(2) + "deg");

    var sheen = el.querySelector(".ma-3d-sheen");
    if (sheen) {
      sheen.style.opacity = "1";
      sheen.style.setProperty("--ma-3d-sheen-x", (px * 100).toFixed(1) + "%");
      sheen.style.setProperty("--ma-3d-sheen-y", (py * 100).toFixed(1) + "%");
    }
  }

  function onTiltLeave(el) {
    el.style.setProperty("--ma-3d-tilt-x", "0deg");
    el.style.setProperty("--ma-3d-tilt-y", "0deg");
    var sheen = el.querySelector(".ma-3d-sheen");
    if (sheen) sheen.style.opacity = "0";
  }

  function bindTilt(el) {
    if (tiltBound) {
      if (tiltBound.has(el)) return;
      tiltBound.add(el);
    } else if (el.dataset.ma3dTiltBound) {
      return;
    } else {
      el.dataset.ma3dTiltBound = "true";
    }
    el.addEventListener("mousemove", function (e) { onTiltMove(el, e); });
    el.addEventListener("mouseleave", function () { onTiltLeave(el); });
    el.addEventListener("blur", function () { onTiltLeave(el); });
  }

  function scanTilt(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    var els = context.querySelectorAll(TILT_SELECTOR);
    for (var i = 0; i < els.length; i++) bindTilt(els[i]);
  }

  function readyTilt() {
    scanTilt(document);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", readyTilt);
  } else {
    readyTilt();
  }

  /* Theme Editor: newly-added/rearranged blocks bring fresh DOM nodes
     that need their own listeners — existing nodes are a no-op thanks
     to the bound-check above. */
  document.addEventListener("shopify:section:load", function (e) {
    scanTilt(e.target);
  });
  document.addEventListener("shopify:block:select", function (e) {
    scanTilt(e.target);
  });

  window[namespace] = Object.assign(window[namespace] || {}, {
    is3DEnabled: is3DEnabled,
    shouldAutoAnimate3D: shouldAutoAnimate3D
  });
})();
